import React, { Fragment, ReactNode } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import HouseSimpleIcon from '../components/icons/HouseSimpleIcon';
import ArrowsLeftRightIcon from '../components/icons/ArrowsLeftRightIcon';
import StoreFrontIcon from '../components/icons/StoreFrontIcon';
import CollaboratorsIcon from '../components/icons/CollaboratorsIcon';
import Header from '../components/Header';
import NotepadIcon from '../components/icons/NotepadIcon';
import { PiHandLight, PiLinkSimpleHorizontalBold } from 'react-icons/pi';
import { IoIosArrowDown, IoIosArrowUp, IoIosLogOut, IoMdPhonePortrait } from 'react-icons/io';
import useAuth from '../hooks/useAuth';
import { FiBarChart2 } from 'react-icons/fi';
import { RoleValidator } from '../components/RoleValidator';
import { Role } from '../domain/Role';

interface NavSubItem {
  name: string;
  link: string;
}

interface NavItems {
  name: string;
  icon: ReactNode;
  link: string;
  AllowedRoles?: Role[];
  children?: NavSubItem[];
}

const HOME_PATH = '/home';

function Root() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const imageSrc = useBreakpointValue({
    'base': 'logo.png',
    'md': 'logo@2x.png',
    'lg': 'logo@3x.png',
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCancelLogout = () => {
    onClose();
    navigate(HOME_PATH);
  };

  const navItems: NavItems[] = [
    {
      name: 'Inicio',
      icon: <HouseSimpleIcon />,
      link: HOME_PATH,
    },
    {
      name: 'Transacciones',
      icon: <ArrowsLeftRightIcon />,
      link: '/transactions',
    },
    {
      name: 'Liquidaciones',
      icon: <FiBarChart2 />,
      link: '/settlements',
      AllowedRoles: [Role.auditor, Role.branch_owner],
    },
    {
      name: 'Mi comercio',
      icon: <StoreFrontIcon />,
      link: '/merchants',
      AllowedRoles: [Role.branch_owner],
    },
    {
      name: 'Comercios',
      icon: <StoreFrontIcon />,
      link: '/auditor-merchants',
      AllowedRoles: [Role.auditor],
    },
    {
      name: 'Usuarios',
      icon: <CollaboratorsIcon />,
      link: '/collaborators',
    }
    // {
    //   name: 'Facturación',
    //   icon: <NotepadIcon />,
    //   link: '/bills',
    // },
    // {
    //   name: 'Terminales',
    //   icon: <IoMdPhonePortrait />,
    //   link: '/terminals',
    // },
    // Comentado a petición: Links de Pago queda pausado, será otra feature más adelante.
    // {
    //   name: 'Links',
    //   icon: <PiLinkSimpleHorizontalBold />,
    //   link: '/links',
    //   children: [
    //     { name: 'Generación', link: '/links/generacion' },
    //     { name: 'Consulta', link: '/links/consulta' },
    //   ],
    // },
  ];

  return (
    <Flex height="100vh" p={'24px'}>
      <Modal isOpen={isOpen} onClose={handleCancelLogout}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="neutral.900">Cerrar sesión</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>¿Estas seguro que deseas cerrar sesión? </Text>
          </ModalBody>

          <ModalFooter>
            <Button marginRight="l" onClick={handleLogout}>Cerrar sesión</Button>
            <Button backgroundColor="destructive.700" color="white" onClick={handleCancelLogout}>Cancelar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* Side Nav */}
      <Box as="nav" width="200px" height="100%" p={4}>
        <VStack align="start" height="100%" gap={2}>
          <Image src={imageSrc} alt="Logo de wuzi" objectFit="cover" height="24px" paddingLeft={'10px'}
                 marginBottom={5} />
          {navItems.map((item, i) => (
            <Fragment key={i}>
              { item.AllowedRoles ?
                  <RoleValidator allowedRoles={item.AllowedRoles}>
                    <NavItem item={item} />
                  </RoleValidator>
                :
                  <NavItem item={item} />
              }
            </Fragment>
          ))}

          <Box marginTop="auto">
            {/*<MenuLink icon={<PiHandLight />} name="Soporte" />*/}
          </Box>
          <Box marginTop="xl">
            <MenuLink icon={<IoIosLogOut />} name="Cerrar Sesión" onClick={onOpen} />
          </Box>

        </VStack>
      </Box>

      {/* Main Content */}
      <Flex
        flex="1"
        direction="column"
        bg="white"
        border="1px"
        borderColor="neutral.300"
        borderRadius="16px"
        marginLeft={'24px'}
        overflow="hidden"
        padding={'20px'}
      >
        {/* Header */}
        <Box>
          <Header />
        </Box>

        {/* Outlet - contenido dinámico */}
        <Box
          flexGrow="1"
          marginTop={'20px'}
          overflowY="auto"
        >
          <Outlet />
        </Box>
      </Flex>
    </Flex>
  );
}

interface MenuLinkProps extends React.HTMLProps<any> {
  icon?: ReactNode;
  name: string;
  link?: string;
}

const MenuLink = ({ icon, name, link, ...props }: MenuLinkProps) => {
  const location = useLocation();

  return (
    <Link to={link || ''} {...props}>
      <Flex
        align="center"
        padding={2}
        borderRadius="md"
        background={location.pathname === link ? 'white' : 'transparent'} // Aplica background solo si está activo
        boxShadow={location.pathname === link ? '0px 2px 4px rgba(0, 0, 0, 0.12)' : 'none'} // Aplica box-shadow solo si está activo
        _hover={{ bg: 'gray.100' }} // Estilo al pasar el mouse
        cursor="pointer">
        {icon}
        <Text
          ml={icon ? 2 : 0}
          fontSize='s'
          color={'neutral.600'}>{name}</Text>
      </Flex>
    </Link>
  );
};

const NavItem = ({ item }: { item: NavItems }) => {
  const location = useLocation();
  const isActiveGroup = location.pathname.startsWith(item.link);
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: isActiveGroup });

  if (!item.children) {
    return <MenuLink link={item.link} icon={item.icon} name={item.name} />;
  }

  return (
    <Box width="100%">
      <Flex
        align="center"
        justify="space-between"
        padding={2}
        borderRadius="md"
        background={isActiveGroup ? 'white' : 'transparent'}
        boxShadow={isActiveGroup ? '0px 2px 4px rgba(0, 0, 0, 0.12)' : 'none'}
        _hover={{ bg: 'gray.100' }}
        cursor="pointer"
        onClick={onToggle}>
        <Flex align="center">
          {item.icon}
          <Text ml={2} fontSize='s' color={'neutral.600'}>{item.name}</Text>
        </Flex>
        {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
      </Flex>

      {isOpen &&
        <VStack align="start" gap={1} marginTop={1} paddingLeft={4} borderLeft="1px solid" borderColor="neutral.300">
          {item.children.map((child, i) => (
            <MenuLink key={i} link={child.link} name={child.name} />
          ))}
        </VStack>
      }
    </Box>
  );
};

export default Root;
